import { AddIcon } from '@chakra-ui/icons';
import {
    Button,
    Center,
    Flex,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverFooter,
    PopoverHeader,
    PopoverTrigger,
    Select,
    Stack,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { DocumentNode, useMutation, useQuery } from '@apollo/client';
import usePipelineStore from '../../stores/pipeline-store';
import useRuntimeStore from '../../stores/runtime-store';
import { CREATE_INSTANCE_MUTATION, DELETE_INSTANCE_MUTATION, FIND_INSTANCES_QUERY } from '../../api/instances-api';

import { InstancesTable } from '../InstancesTable';

function NewInstancePopover() {
    const [launchVars, setLaunchVars] = useState({} as any);
    const { onOpen, onClose, isOpen } = useDisclosure();
    const { pipeline, setPipeline }: { pipeline: PipelineJson; setPipeline: any } = usePipelineStore(
        (state: any) => state
    );

    const [createNewInstance] = useMutation(CREATE_INSTANCE_MUTATION, {
        variables: {
            launchTime: new Date(),
            launchVars: launchVars,
            pipeline,
        },
        onCompleted: (data) => {
            const instanceId = data?.response?.id;
            if (!instanceId) {
                alert("Error: didn't get id");
            }

            const newPipeline: PipelineJson = JSON.parse(JSON.stringify(pipeline));
            newPipeline.editorSettings.debugInstancesIds.unshift(instanceId);
            setPipeline(newPipeline);
        },
        onError: (err) => {
            alert(err);
        },
    });

    useEffect(() => {
        const newLaunchVars: any = {};
        pipeline.editorSettings.varsOrder.forEach((varId) => {
            const varObj = pipeline.vars[varId];

            if (varObj.isLaunchVar) {
                newLaunchVars[varObj.name] = varObj.defaultValue;
            }
        });
        setLaunchVars(newLaunchVars);
    }, []);
    return (
        <Popover isLazy onClose={onClose} isOpen={isOpen} onOpen={onOpen}>
            <PopoverTrigger>
                <Button
                    backgroundColor="white"
                    _hover={{
                        backgroundColor: 'white',
                        color: 'black',
                    }}
                    _focus={{ backgroundColor: 'white' }}
                    _active={{ backgroundColor: 'white' }}
                    color={'grey'}
                    leftIcon={<AddIcon />}
                    height="1.75rem"
                >
                    Add instance
                </Button>
            </PopoverTrigger>
            <PopoverContent backdropBlur={'lg'} w={'600px'}>
                <PopoverArrow />

                <PopoverHeader>Create new Instance</PopoverHeader>
                <PopoverBody>
                    <Stack maxH={300} overflow={'scroll'}>
                        {pipeline.editorSettings.varsOrder.map((varId: string) => {
                            const varObj = pipeline.vars[varId];

                            if (!varObj.isLaunchVar) return;
                            return (
                                <Flex key={varId} alignItems={'center'}>
                                    <Text mr={2}>{varObj.name}:</Text>{' '}
                                    {varObj.type == 'STRING' && (
                                        <Input
                                            value={launchVars[varObj.name]}
                                            type={'string'}
                                            onChange={({ target: { value } }: any) => {
                                                let newLaunchVars = JSON.parse(JSON.stringify(launchVars));
                                                newLaunchVars[varObj.name] = value;

                                                setLaunchVars(newLaunchVars);
                                            }}
                                        />
                                    )}
                                    {varObj.type == 'NUMBER' && (
                                        <NumberInput
                                            value={launchVars[varObj.name]}
                                            ml={2}
                                            onChange={(value: any) => {
                                                let newLaunchVars = JSON.parse(JSON.stringify(launchVars));
                                                newLaunchVars[varObj.name] = value;

                                                setLaunchVars(newLaunchVars);
                                            }}
                                        >
                                            <NumberInputField />
                                            <NumberInputStepper>
                                                <NumberIncrementStepper />
                                                <NumberDecrementStepper />
                                            </NumberInputStepper>
                                        </NumberInput>
                                    )}
                                    {varObj.type == 'BOOLEAN' && (
                                        <Select
                                            width={300}
                                            ml={2}
                                            value={launchVars[varObj.name] ? 'true' : 'false'}
                                            onChange={({ target: { value } }: any) => {
                                                let newLaunchVars = JSON.parse(JSON.stringify(launchVars));
                                                newLaunchVars[varObj.name] = value == 'true';

                                                setLaunchVars(newLaunchVars);
                                            }}
                                        >
                                            <option value="true">True</option>
                                            <option value="false">False</option>
                                        </Select>
                                    )}
                                </Flex>
                            );
                        })}
                    </Stack>
                </PopoverBody>
                <PopoverFooter>
                    <Center>
                        <Button
                            onClick={() => {
                                createNewInstance();
                                onClose();
                            }}
                        >
                            Create Instance
                        </Button>
                    </Center>
                </PopoverFooter>
            </PopoverContent>
        </Popover>
    );
}

export function DebugModal() {
    const [allInstances, setAllInstances] = useState([] as PipelineInstance[]);
    const { debugModalIsOpen, setDebugModalIsOpen } = useRuntimeStore((state: any) => state);
    const { pipeline, setPipeline }: { pipeline: PipelineJson; setPipeline: any } = usePipelineStore(
        (state: any) => state
    );

    const [deleteInstance] = useMutation(DELETE_INSTANCE_MUTATION, {
        onCompleted: (data) => {
            const instanceId = data?.response?.id;
            if (!instanceId) return;

            const newPipeline: PipelineJson = JSON.parse(JSON.stringify(pipeline));
            newPipeline.editorSettings.debugInstancesIds.splice(
                newPipeline.editorSettings.debugInstancesIds.indexOf(instanceId),
                1
            );

            setPipeline(newPipeline);
        },
        onError: (err) => {
            alert(err);
        },
    });

    useQuery(FIND_INSTANCES_QUERY, {
        variables: {
            instanceIds: pipeline.editorSettings.debugInstancesIds,
        },
        onCompleted: (data) => {
            const instancesArray = data?.response as PipelineInstance[];
            if (instancesArray != undefined) {
                setAllInstances(instancesArray);
            }
        },
        pollInterval: 2000,
    });

    return (
        <Modal
            isOpen={debugModalIsOpen}
            size={'6xl'}
            onClose={() => {
                setDebugModalIsOpen(false);
            }}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Debug</ModalHeader>
                <ModalCloseButton
                    onClick={() => {
                        setDebugModalIsOpen(false);
                    }}
                />

                <ModalBody>
                    <InstancesTable instances={allInstances} deleteInstance={deleteInstance} />
                </ModalBody>

                <ModalFooter justifyContent={'center'} marginBottom={3}>
                    <Center>
                        <NewInstancePopover />
                    </Center>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
